import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLocked(false);
    setLoading(true);

    try {
      await login(email, password);
      navigate('/explore/octants');
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.detail || 'Login failed';
      
      if (status === 423) {
        setIsLocked(true);
      }
      
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="glass-card rounded-xl p-8 w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img 
            src="/logo.png" 
            alt="Rook Games" 
            className="h-20 w-20 object-contain mb-4"
            data-testid="login-logo"
          />
          <h1 className="text-4xl font-bold text-center text-primary">KRAKEN</h1>
          <p className="text-center text-muted-foreground mt-2">Login to explore the cosmos</p>
        </div>

        {error && (
          <div 
            className={`border rounded-lg p-3 mb-4 ${
              isLocked 
                ? 'bg-yellow-500/10 border-yellow-500 text-yellow-400' 
                : 'bg-destructive/10 border-destructive text-destructive'
            }`} 
            data-testid="error-message"
          >
            {isLocked && (
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="font-semibold">Account Locked</span>
              </div>
            )}
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-cyan-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-foreground"
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Password</label>
              <Link 
                to="/forgot-password" 
                className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-cyan-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-foreground"
              required
              data-testid="password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg btn-glow disabled:opacity-50 transition-all"
            data-testid="login-button"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Login;
